import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { toast } from 'react-toastify';
import { hostnameFromUrl } from 'services/hostname';
import { authConfig, ensureOwnUniversalTemplate, fetchOverview } from 'services/integrations';
import { KindLogo } from './integrationKinds';

/**
 * "Votre site" (`/integrations/new-site`): the step before installing the form. The seller's
 * "Mon formulaire Leadrs" integration is only created (or reused — one covers every site) once a
 * site address has been given, so no empty integration is left behind by someone who just looked.
 */
const NewSiteIntegration = () => {
    const navigate = useNavigate();
    const [site, setSite] = useState('');
    const [knownSites, setKnownSites] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([axios.get(`${CONFIG.apiUrl}/user/me`, authConfig()), fetchOverview()])
            .then(([me, overview]) => {
                const hosts = overview.filter((t) => t.isUniversal).flatMap((t) => t.sites.map((s) => s.hostname));
                setKnownSites(hosts);
                // Prefill with the profile's website, unless that site is already set up.
                const website = me.data?.website || '';
                if (website && !hosts.includes(hostnameFromUrl(website))) setSite(website);
            })
            .catch(() => {});
    }, []);

    const host = hostnameFromUrl(site);
    // A real domain: something.tld, no spaces.
    const valid = /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(host);
    const alreadyThere = valid && knownSites.includes(host);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!valid || saving) return;
        setSaving(true);
        try {
            const id = await ensureOwnUniversalTemplate();
            // A site the seller had deleted is tracked again once they add it back.
            await axios.post(`${CONFIG.apiUrl}/form-template/${id}/site/restore`, { hostname: host }, authConfig()).catch(() => {});
            navigate(`/integrations/${id}?tab=installation&step=install&site=${encodeURIComponent(host)}`);
        } catch (err: any) {
            console.error('Error creating the site integration:', err);
            toast.error(err?.response?.data?.message || 'Une erreur est survenue, réessayez.');
            setSaving(false);
        }
    };

    return (
        <div className="mx-auto flex max-w-[620px] flex-col gap-5 pb-16 pt-2 font-hanken text-[#1A1613]">
            <button onClick={() => navigate('/integrations/start')} className="self-start text-[13.5px] font-semibold text-[#241C5C] hover:underline">
                ← Ajouter une intégration
            </button>
            <div className="flex items-center gap-3">
                <KindLogo kind="site" />
                <div>
                    <h1 className="font-newsreader text-[30px] font-medium leading-tight tracking-[-0.02em] text-[#14110E]">Votre site</h1>
                    <p className="text-[14px] text-[#6B6357]">Sur quel site voulez-vous installer le formulaire Leadrs ?</p>
                </div>
            </div>

            <form onSubmit={submit} className="flex flex-col gap-3 rounded-[18px] border border-[#EDE7DE] bg-white p-6">
                <label htmlFor="new-site" className="text-[13.5px] font-semibold text-[#3B352C]">
                    Adresse du site
                </label>
                <input
                    id="new-site"
                    autoFocus
                    value={site}
                    onChange={(e) => setSite(e.target.value)}
                    placeholder="exemple.fr"
                    inputMode="url"
                    autoComplete="url"
                    className="rounded-[10px] border border-[#DDD5C9] bg-white px-[13px] py-[11px] text-[14.5px] outline-none focus:border-[#241C5C]"
                />
                <p className="text-[12.5px] text-[#8A8172]">
                    {alreadyThere
                        ? `${host} est déjà relié : vous retrouverez son installation à l'étape suivante.`
                        : site && !valid
                        ? 'Indiquez une adresse complète, par exemple exemple.fr.'
                        : "Le site n'a pas besoin d'être déjà en ligne : vous pourrez publier plus tard."}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-3 border-t border-[#F1ECE4] pt-4">
                    <button
                        type="submit"
                        disabled={!valid || saving}
                        className="rounded-[10px] bg-[#241C5C] px-4 py-2.5 text-[13.5px] font-semibold text-white transition-colors hover:bg-[#332a75] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {saving ? 'Un instant…' : 'Continuer vers l’installation →'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default NewSiteIntegration;
